import React, {Component} from 'react';
import {action, observable, computed} from 'mobx';
import {inject, observer} from 'mobx-react';
import {
  Button, FormInput, FormSelect, FormTextarea,
  Modal, ModalBody, ModalHeader, ModalFooter
} from 'shards-react';
import FeatherIcon from 'feather-icons-react';
import {toast} from 'react-toastify';
import PageTitle from '../../../components/common/PageTitle';
import './seoProductList.scss';

// SEO 필드 메타데이터 (라벨/타입/가이드/검증 룰)
const SEO_FIELDS = [
  {key: 'meta_title',          short: '타이틀', label: '브라우저 타이틀',         type: 'text',     min: 10, max: 60,  helper: '검색 결과·브라우저 탭 제목. 30~60자 권장.'},
  {key: 'meta_description',    short: '설명',   label: '메타 설명',               type: 'textarea', min: 50, max: 160, helper: '검색 결과 요약 문구. 50~160자 권장.', rows: 3},
  {key: 'meta_keywords',       short: '키워드', label: '키워드',                  type: 'text',     min: 0,  max: 200, helper: '쉼표로 구분된 키워드 목록.'},
  {key: 'meta_author',         short: 'Author', label: 'Author',                  type: 'text',     min: 0,  max: 100, helper: '제조자 또는 등록자.'},
  {key: 'meta_alt',            short: 'Alt',    label: '이미지 Alt 텍스트',       type: 'text',     min: 0,  max: 100, helper: '이미지 대체 텍스트 (검색·접근성).'},
  {key: 'search_engine_exposure', short: '노출', label: '검색엔진 노출',          type: 'select',   helper: '"노출안함" 시 검색엔진에 색인되지 않습니다.'}
];

const SE_OPTIONS = [
  {value: 'T', label: '노출함'},
  {value: 'F', label: '노출안함'}
];

// 필드별 채워짐 여부 판정
// search_engine_exposure 특수 처리:
//   카페24 기본값이 노출(T)이므로 미설정(null/undef)도 노출 상태로 간주.
//   명시적 'F'(노출 안함)일 때만 비활성으로 표시 → 편집 모달의 select 표시와 일치.
const isFilled = (field, value) => {
  if (field.key === 'search_engine_exposure') {
    return value !== 'F';
  }
  return !!(value && String(value).trim().length > 0);
};

// 진단 점수: 6개 필드 중 채워진 비율
const getCompletion = (seo) => {
  if (!seo) return 0;
  const filled = SEO_FIELDS.filter(f => isFilled(f, seo[f.key])).length;
  return Math.round((filled / SEO_FIELDS.length) * 100);
};

@inject(({teamUser}) => ({isLogin: teamUser.isLogin, me: teamUser.me}))
@inject(({common}) => ({getDoc: common.getDoc, updateDoc: common.updateDoc}))
@inject(({product}) => ({getProductList: product.getProductList}))
@observer
class SeoProductList extends Component {

  // ===== 리스트 상태 =====
  @observable list = {docs: [], totalDocs: 0, totalPages: 1, page: 1, limit: 20};
  @observable isLoading = false;
  @observable isLoadingSeo = false;
  @observable keyword = '';
  @observable statusFilter = 'all'; // all | missingTitle | missingDesc | missingAlt | hidden | incomplete
  @observable listParams = {page: 1, limit: 20};

  // 상품번호별 SEO 캐시 (Map은 mobx 호환 위해 plain object)
  @observable seoCache = {};

  // 전체 카탈로그 분석 상태
  @observable allProducts = [];
  @observable analysis = {
    isRunning: false,
    isComplete: false,
    isCancelling: false,
    total: 0,         // 전체 상품 수
    productsLoaded: 0, // 상품 목록 로드된 수
    analyzed: 0,      // SEO 분석 완료된 수 (실제 응답 기반 — 청크 단위로 점프)
    mode: 'cache',    // 'cache' (캐시 로드) | 'cafe24' (재분석)
    lastSyncedAt: null // 캐시에서 가장 최근 syncedAt
  };

  // 진행률 부드러운 가속용 — 응답 도착 시 실제값으로 보정
  @observable optimisticAnalyzed = 0;
  optimReal = 0;          // 마지막 실제 analyzed 값
  optimRealAt = 0;        // 마지막 실제 업데이트 시각
  optimRaf = null;

  // ===== 분석 페이싱 설정 (초 단위) =====
  ANALYSIS_BATCH_SIZE = 40;            // 1 batch 당 상품 수 (forceReanalyze CHUNK)
  ANALYSIS_BATCH_RESPONSE_SEC = 21;    // 1 batch 응답 시간 추정값 (초). 시각 진행률 계산에만 사용.

  // ===== 일괄 설정 페이싱 설정 (초 단위) =====
  // batch 처리 모델: ANALYSIS_BATCH_SIZE 단위로 backend에 전송, backend는 10개씩 chunk + 1초 sleep
  // → 40개 batch 응답 ≈ 4초 (chunk 4 × 1초 페이싱 + 응답 시간)
  BULK_BATCH_RESPONSE_SEC = 22;         // 1 batch 응답 시간 추정값 (초). 시각 진행률 계산에만 사용.
  // 일괄 처리 진행률 부드러운 가속용
  @observable bulkOptimisticDone = 0;
  bulkOptimReal = 0;
  bulkOptimRealAt = 0;
  bulkOptimRaf = null;

  // 유료 사용자만 편집/일괄설정 가능 (진단/조회는 무료 허용)
  // 14일 무료 체험(shop.orderType === 'free')은 유료 기능 사용 불가
  get isPaidUser() {
    const me = this.props.me;
    if (!me || !me.isPaidShop) return false;
    if (me.shop && me.shop.orderType === 'free') return false;
    return true;
  }

  // ===== 다중 선택 =====
  @observable selectedIds = []; // platformProductId 배열

  // ===== 사이드 패널 (편집) =====
  @observable panel = {
    open: false,
    productIndex: -1,  // filteredDocs 기준 인덱스
    seo: {},
    isLoading: false,
    isSaving: false,
    isDirty: false
  };

  // ===== 패널 내 리뷰 인사이트 =====
  @observable panelInsight = {
    isLoading: false,
    minRating: 4,                 // 기본 4점 이상
    prependProductName: true,     // 상품명을 앞에 표기 (기본 ON)
    data: null                    // {reviewCount, avgRating, topKeywords, bestSnippets, generated}
  };

  // ===== 일괄 설정 =====
  @observable bulkModal = {
    open: false,
    scope: 'all', // 'all' | 'page' | 'selected'
    mode: 'fillEmpty', // 'fillEmpty' | 'overwrite'
    source: 'template', // 'template' (기존) | 'reviewBased' (리뷰 데이터 기반 자동)
    reviewMinRating: 4,
    reviewPrependProductName: true, // 리뷰 기반 — 상품명 앞에 표기 (기본 ON)
    enabled: {
      meta_title: true,
      meta_description: false,
      meta_keywords: false,
      meta_author: false,
      meta_alt: true,
      search_engine_exposure: false
    },
    template: {
      meta_title: '{상품명}',
      meta_description: '',
      meta_keywords: '',
      meta_author: '',
      meta_alt: '{상품명}',
      search_engine_exposure: 'T'
    }
  };

  @observable bulkProgress = {
    open: false, total: 0, done: 0, success: 0, failed: 0,
    failedList: [], isRunning: false, isCancelling: false, isCompleted: false
  };

  // 일괄 설정 / 재분석: 모두 batch 단위로 백엔드 호출. 페이싱은 backend chunk + sleep이 담당.

  // ===== Optimistic 진행률 (부드러운 가속 + 응답 보정) =====
  startOptimisticTick = () => {
    this.optimReal = 0;
    this.optimRealAt = Date.now();
    this.optimisticAnalyzed = 0;
    if (this.optimRaf) cancelAnimationFrame(this.optimRaf);
    const tick = () => {
      const a = this.analysis;
      // 카페24 모드 + 실행 중에만 동작. 아니면 정지.
      if (!a.isRunning || a.mode !== 'cafe24') {
        this.optimRaf = null;
        return;
      }
      const realNow = a.analyzed || 0;
      // 실제 값이 변하면 보정: 즉시 그 값으로 스냅 + 시작 시각 갱신
      if (realNow !== this.optimReal) {
        this.optimReal = realNow;
        this.optimRealAt = Date.now();
      }
      // 마지막 실제 값 + 경과 시간 × 추정 속도
      // msPerItem = (batch 응답 초 × 1000) / batch 사이즈
      const msPerItem = (this.ANALYSIS_BATCH_RESPONSE_SEC * 1000) / this.ANALYSIS_BATCH_SIZE;
      const elapsed = Date.now() - this.optimRealAt;
      const estimated = this.optimReal + (elapsed / msPerItem);
      const total = a.total || 0;
      // 상한: 한 batch 분량을 미리 못 채우게 막아 응답 직전 100% 조기 도달 방지
      const nextChunkCap = this.optimReal + (this.ANALYSIS_BATCH_SIZE - 0.5);
      this.optimisticAnalyzed = Math.max(this.optimReal, Math.min(total, nextChunkCap, estimated));
      this.optimRaf = requestAnimationFrame(tick);
    };
    this.optimRaf = requestAnimationFrame(tick);
  };

  stopOptimisticTick = () => {
    if (this.optimRaf) {
      cancelAnimationFrame(this.optimRaf);
      this.optimRaf = null;
    }
  };

  // 일괄 설정용 optimistic tick — batch 응답 기반 가속 (재분석 tick과 동일 패턴)
  startBulkOptimisticTick = () => {
    this.bulkOptimReal = 0;
    this.bulkOptimRealAt = Date.now();
    this.bulkOptimisticDone = 0;
    if (this.bulkOptimRaf) cancelAnimationFrame(this.bulkOptimRaf);
    const tick = () => {
      const p = this.bulkProgress;
      if (!p.isRunning) {
        this.bulkOptimRaf = null;
        return;
      }
      const realNow = p.done || 0;
      if (realNow !== this.bulkOptimReal) {
        this.bulkOptimReal = realNow;
        this.bulkOptimRealAt = Date.now();
      }
      // msPerItem = (batch 응답 초 × 1000) / batch 사이즈
      const msPerItem = (this.BULK_BATCH_RESPONSE_SEC * 1000) / this.ANALYSIS_BATCH_SIZE;
      const elapsed = Date.now() - this.bulkOptimRealAt;
      const estimated = this.bulkOptimReal + (elapsed / msPerItem);
      const total = p.total || 0;
      // 한 batch 분량을 미리 못 채우게 cap (응답 직전 100% 조기 도달 방지)
      const cap = this.bulkOptimReal + (this.ANALYSIS_BATCH_SIZE - 0.5);
      this.bulkOptimisticDone = Math.max(this.bulkOptimReal, Math.min(total, cap, estimated));
      this.bulkOptimRaf = requestAnimationFrame(tick);
    };
    this.bulkOptimRaf = requestAnimationFrame(tick);
  };

  stopBulkOptimisticTick = () => {
    if (this.bulkOptimRaf) {
      cancelAnimationFrame(this.bulkOptimRaf);
      this.bulkOptimRaf = null;
    }
  };

  formatRelativeTime = (date) => {
    if (!date) return '';
    const d = (date instanceof Date) ? date : new Date(date);
    const diff = Math.max(0, Date.now() - d.getTime());
    const sec = Math.floor(diff / 1000);
    if (sec < 60) return '방금 전';
    if (sec < 3600) return `${Math.floor(sec / 60)}분 전`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}시간 전`;
    const days = Math.floor(sec / 86400);
    if (days < 30) return `${days}일 전`;
    return d.toLocaleDateString('ko-KR');
  };

  componentDidMount() {
    this.loadList();
    // 백그라운드에서 캐시 로드 (카페24 호출 없음)
    setTimeout(() => this.loadCachedAnalysis(), 200);
  }

  componentWillUnmount() {
    this.analysis.isCancelling = true;
    this.stopOptimisticTick();
  }

  // ============ 리스트 로딩 ============

  @action
  loadList = async () => {
    if (!this.props.me || !this.props.me.store) return;
    this.isLoading = true;
    try {
      const params = {
        store_id: this.props.me.store._id,
        shopNo: this.props.me.selShopNo || 1,
        page: this.listParams.page,
        limit: this.listParams.limit,
        searchParams: {keyword: this.keyword || ''}
      };
      const res = await this.props.getProductList(params);
      if (res && res.docs) {
        this.list = res;
        this.fetchSeoBatch(res.docs);
      }
    } catch (e) {
      console.error(e);
      toast.error('상품 목록을 불러오지 못했습니다.');
    } finally {
      this.isLoading = false;
    }
  };

  @action
  fetchSeoBatch = async (docs) => {
    // 페이지 변경 시 호출 — 캐시에서만 로드 (카페24 호출 없음)
    if (!docs || docs.length === 0) return;
    const productNos = docs
      .map(d => d.platformProductId)
      .filter(no => !!no && this.seoCache[no] === undefined);
    if (productNos.length === 0) return;
    this.isLoadingSeo = true;
    try {
      const param = {
        store_id: this.props.me.store._id,
        shopNo: this.props.me.selShopNo || 1,
        product_nos: productNos
      };
      const res = await this.props.getDoc('getCafe24ProductSeoCachedBatch', param);
      const arr = (res && res.data) ? res.data : [];
      arr.forEach(item => {
        if (item && item.product_no != null) {
          // 캐시 미스도 빈 객체로 표시 (UI에서 "분석 필요" 처리는 별도)
          this.seoCache[item.product_no] = item.seo || {};
        }
      });
    } catch (e) {
      console.error('SEO cached batch fetch failed', e);
    } finally {
      this.isLoadingSeo = false;
    }
  };

  // ============ 전체 카탈로그 분석 ============
  // 두 모드:
  //   - loadCachedAnalysis: 페이지 진입 시 자동. Mongo 캐시만 사용 (카페24 호출 zero)
  //   - forceReanalyze:    "재분석" 버튼 클릭 시. 카페24 API에서 신선한 데이터 페치 + 캐시 갱신

  loadAllProductsFromMongo = async () => {
    // 모든 페이지의 상품 목록을 Mongo에서 누적 로드
    const limit = 100;
    let page = 1, totalPages = 1;
    const accumulated = [];
    do {
      if (this.analysis.isCancelling) break;
      const params = {
        store_id: this.props.me.store._id,
        shopNo: this.props.me.selShopNo || 1,
        page, limit,
        searchParams: {keyword: ''}
      };
      const res = await this.props.getProductList(params);
      if (!res || !res.docs) break;
      accumulated.push(...res.docs);
      totalPages = res.totalPages || 1;
      this.analysis.total = res.totalDocs || accumulated.length;
      this.analysis.productsLoaded = accumulated.length;
      this.allProducts = accumulated.slice();
      page++;
      if (page > totalPages) break;
    } while (true);
    return accumulated;
  };

  @action
  loadCachedAnalysis = async () => {
    // 페이지 진입 시 자동 호출. DB 캐시만 사용. 카페24 호출 X.
    if (!this.props.me || !this.props.me.store) return;
    if (this.analysis.isRunning) return;
    this.analysis.isRunning = true;
    this.analysis.isComplete = false;
    this.analysis.isCancelling = false;
    this.analysis.productsLoaded = 0;
    this.analysis.analyzed = 0;
    this.analysis.mode = 'cache';

    try {
      const accumulated = await this.loadAllProductsFromMongo();
      if (this.analysis.isCancelling) { this.analysis.isRunning = false; return; }

      // DB 캐시에서 SEO 일괄 조회 (200개씩)
      const productNos = accumulated.map(p => p.platformProductId).filter(no => !!no);
      const CHUNK = 200;
      let lastSynced = null;
      for (let i = 0; i < productNos.length; i += CHUNK) {
        if (this.analysis.isCancelling) break;
        const slice = productNos.slice(i, i + CHUNK);
        try {
          const res = await this.props.getDoc('getCafe24ProductSeoCachedBatch', {
            store_id: this.props.me.store._id,
            shopNo: this.props.me.selShopNo || 1,
            product_nos: slice
          });
          const items = (res && res.data) ? res.data : [];
          // forEach 콜백이 outer for 루프 안에 있어 no-loop-func 경고 → for...of 로 변환 (closure 미생성)
          for (const item of items) {
            if (item && item.product_no != null) {
              this.seoCache[item.product_no] = item.seo || {};
              if (item.syncedAt) {
                const t = new Date(item.syncedAt).getTime();
                if (!lastSynced || t > lastSynced) lastSynced = t;
              }
            }
          }
        } catch (e) {
          console.warn('cached batch chunk failed', e);
        }
        this.analysis.analyzed = productNos.filter(no => this.seoCache[no] !== undefined).length;
      }

      this.analysis.lastSyncedAt = lastSynced ? new Date(lastSynced) : null;
      this.analysis.isComplete = !this.analysis.isCancelling;
    } catch (e) {
      console.error(e);
    } finally {
      this.analysis.isRunning = false;
    }
  };

  @action
  forceReanalyze = async () => {
    // "재분석" 버튼 클릭 시. 카페24 API에서 신선한 데이터 페치 + DB 캐시 갱신
    if (!this.props.me || !this.props.me.store) return;
    if (this.analysis.isRunning) return;
    this.analysis.isRunning = true;
    this.analysis.isComplete = false;
    this.analysis.isCancelling = false;
    this.analysis.productsLoaded = 0;
    this.analysis.analyzed = 0;
    this.analysis.mode = 'cafe24';
    // in-memory 캐시 초기화 (cafe24에서 가져온 신선한 데이터로 덮어쓰기 위함)
    this.seoCache = {};

    try {
      const accumulated = await this.loadAllProductsFromMongo();
      if (this.analysis.isCancelling) { this.analysis.isRunning = false; return; }

      const productNos = accumulated.map(p => p.platformProductId).filter(no => !!no);
      // 카페24 페치 단계 진입 — optimistic tick 시작
      this.startOptimisticTick();
      // 재분석: 응답 기반 페이싱. 이전 batch 응답이 와야 다음 batch 발사 (await로 자연스럽게 직렬화).
      const CHUNK = this.ANALYSIS_BATCH_SIZE;
      for (let i = 0; i < productNos.length; i += CHUNK) {
        if (this.analysis.isCancelling) break;
        const slice = productNos.slice(i, i + CHUNK);
        try {
          const res = await this.props.getDoc('getCafe24ProductSeoBatch', {
            store_id: this.props.me.store._id,
            shopNo: this.props.me.selShopNo || 1,
            product_nos: slice
          });
          const items = (res && res.data) ? res.data : [];
          let rateLimitHits = 0;
          items.forEach(item => {
            if (item && item.product_no != null) {
              this.seoCache[item.product_no] = item.seo || {};
              if (item.error && /too\s*much\s*requests?/i.test(item.error)) rateLimitHits++;
            }
          });
          if (rateLimitHits > 0) {
            toast.error(`카페24 rate limit으로 ${rateLimitHits}건 실패. 잠시 후 다시 재분석을 시도하세요.`);
          }
        } catch (e) {
          console.warn('SEO batch chunk failed', e);
        }
        this.analysis.analyzed = productNos.filter(no => this.seoCache[no] !== undefined).length;
      }

      this.analysis.lastSyncedAt = new Date();
      this.analysis.isComplete = !this.analysis.isCancelling;
      // 완료 시 optimistic을 실제 값과 일치시키고 tick 정지
      this.optimisticAnalyzed = this.analysis.analyzed;
      this.stopOptimisticTick();
      if (!this.analysis.isCancelling) {
        toast.success(`재분석 완료 — ${this.analysis.analyzed.toLocaleString()}개 상품 동기화됨`);
      }
    } catch (e) {
      console.error(e);
      toast.error('재분석 중 오류가 발생했습니다.');
      this.stopOptimisticTick();
    } finally {
      this.analysis.isRunning = false;
    }
  };

  @action
  cancelAnalysis = () => { this.analysis.isCancelling = true; };

  @action
  rerunAnalysis = () => {
    // "재분석" 버튼 — 카페24에서 신선한 데이터로 갱신
    if (this.analysis.isRunning) return;
    this.forceReanalyze();
  };

  // ============ 필터링/통계 ============

  // 필터 활성 시 전체 카탈로그(allProducts) 기준으로 필터링 + 클라이언트 페이지네이션
  // 필터 비활성 시 서버 페이지네이션된 list.docs 그대로 사용
  @computed
  get isClientFilterMode() {
    return this.statusFilter !== 'all' && this.allProducts.length > 0;
  }

  @computed
  get filteredAll() {
    if (!this.isClientFilterMode) return [];
    return this.allProducts.filter(p => this.matchFilter(p));
  }

  @computed
  get filteredDocs() {
    if (this.isClientFilterMode) {
      const arr = this.filteredAll;
      const start = (this.listParams.page - 1) * this.listParams.limit;
      return arr.slice(start, start + this.listParams.limit);
    }
    const docs = (this.list && this.list.docs) ? this.list.docs : [];
    return docs.filter(p => this.matchFilter(p));
  }

  @computed
  get effectiveTotal() {
    if (this.isClientFilterMode) return this.filteredAll.length;
    return (this.list && this.list.totalDocs) ? this.list.totalDocs : 0;
  }

  @computed
  get effectiveTotalPages() {
    if (this.isClientFilterMode) {
      return Math.max(1, Math.ceil(this.filteredAll.length / this.listParams.limit));
    }
    return (this.list && this.list.totalPages) ? this.list.totalPages : 1;
  }

  matchFilter = (p) => {
    const seo = this.seoCache[p.platformProductId] || {};
    switch (this.statusFilter) {
      case 'missingTitle':    return !isFilled(SEO_FIELDS[0], seo.meta_title);
      case 'missingDesc':     return !isFilled(SEO_FIELDS[1], seo.meta_description);
      case 'missingAlt':      return !isFilled(SEO_FIELDS[4], seo.meta_alt);
      case 'hidden':          return seo.search_engine_exposure === 'F';
      case 'incomplete':      return getCompletion(seo) < 100;
      default:                return true;
    }
  };

  // 전체 카탈로그 통계 (allProducts + seoCache 기반)
  @computed
  get healthStats() {
    // 분석 시작 전이면 현재 페이지 폴백 (초기 진입 짧은 순간)
    const products = this.allProducts.length > 0
      ? this.allProducts
      : ((this.list && this.list.docs) ? this.list.docs : []);
    const stats = {
      total: this.analysis.total || products.length,
      analyzed: 0,
      complete: 0,
      avgCompletion: 0,
      missing: {meta_title: 0, meta_description: 0, meta_keywords: 0, meta_author: 0, meta_alt: 0},
      hidden: 0
    };
    if (products.length === 0) return stats;
    let sumPct = 0;
    products.forEach(p => {
      const seo = this.seoCache[p.platformProductId];
      if (seo === undefined) return; // 아직 분석 안 됨
      stats.analyzed++;
      const pct = getCompletion(seo);
      sumPct += pct;
      if (pct === 100) stats.complete++;
      ['meta_title', 'meta_description', 'meta_keywords', 'meta_author', 'meta_alt'].forEach(k => {
        const f = SEO_FIELDS.find(x => x.key === k);
        if (!isFilled(f, seo[k])) stats.missing[k]++;
      });
      if (seo.search_engine_exposure === 'F') stats.hidden++;
    });
    stats.avgCompletion = stats.analyzed > 0 ? Math.round(sumPct / stats.analyzed) : 0;
    return stats;
  }

  // ============ 다중 선택 ============

  @action
  toggleSelect = (id) => {
    const idx = this.selectedIds.indexOf(id);
    if (idx >= 0) this.selectedIds.splice(idx, 1);
    else this.selectedIds.push(id);
  };

  @action
  toggleSelectAll = () => {
    const visibleIds = this.filteredDocs.map(p => p.platformProductId);
    const allSelected = visibleIds.every(id => this.selectedIds.includes(id));
    if (allSelected) {
      this.selectedIds = this.selectedIds.filter(id => !visibleIds.includes(id));
    } else {
      const set = new Set([...this.selectedIds, ...visibleIds]);
      this.selectedIds = Array.from(set);
    }
  };

  @action
  clearSelection = () => { this.selectedIds = []; };

  @computed
  get isAllVisibleSelected() {
    const visible = this.filteredDocs;
    if (visible.length === 0) return false;
    return visible.every(p => this.selectedIds.includes(p.platformProductId));
  }

  // ============ 사이드 패널 (편집) ============

  @action
  openPanel = async (productIndex) => {
    const filtered = this.filteredDocs;
    if (productIndex < 0 || productIndex >= filtered.length) return;
    const product = filtered[productIndex];
    this.panel = {open: true, productIndex, seo: {}, isLoading: true, isSaving: false, isDirty: false};
    // 인사이트 초기화 + 백그라운드 로드
    this.panelInsight = {
      isLoading: false,
      minRating: this.panelInsight.minRating || 4,
      prependProductName: this.panelInsight.prependProductName !== false,
      data: null
    };
    // 캐시(seoCache)에서 로드 — 카페24 호출하지 않음
    // 캐시는 페이지 진입 시 loadCachedAnalysis 또는 fetchSeoBatch에서 채워짐
    const cached = this.seoCache[product.platformProductId];
    if (cached !== undefined) {
      this.panel.seo = JSON.parse(JSON.stringify(cached));
    } else {
      // 캐시 미스 — 빈 객체로 시작. 사용자에게 "재분석 권장" 안내는 별도 UI에서.
      this.panel.seo = {};
    }
    this.panel.isLoading = false;
    // 인사이트 비동기 로드 (UI 블로킹 안 함)
    this.loadInsight(product);
  };

  @action
  loadInsight = async (product) => {
    if (!product || !product.platformProductId) return;
    this.panelInsight.isLoading = true;
    try {
      const param = {
        store_id: this.props.me.store._id,
        shopNo: this.props.me.selShopNo || 1,
        platformProductId: product.platformProductId,
        productName: product.productName || '',
        minRating: this.panelInsight.minRating,
        prependProductName: this.panelInsight.prependProductName !== false
      };
      const res = await this.props.getDoc('getProductReviewInsights', param);
      this.panelInsight.data = (res && res.data) ? res.data : null;
    } catch (e) {
      console.error('insight load failed', e);
      this.panelInsight.data = null;
    } finally {
      this.panelInsight.isLoading = false;
    }
  };

  @action
  setInsightMinRating = (rating) => {
    this.panelInsight.minRating = Number(rating);
    const product = this.currentPanelProduct;
    if (product) this.loadInsight(product);
  };

  @action
  togglePanelPrepend = () => {
    this.panelInsight.prependProductName = !this.panelInsight.prependProductName;
    const product = this.currentPanelProduct;
    if (product) this.loadInsight(product);
  };

  @action
  applyInsightToForm = () => {
    const data = this.panelInsight.data;
    if (!data || !data.generated) {
      toast.warn('적용할 데이터가 없습니다.');
      return;
    }
    const g = data.generated;
    const seo = this.panel.seo || {};
    let changed = 0;
    // 비어있는 필드만 채움 (기존 값 유지)
    ['meta_title', 'meta_description', 'meta_keywords', 'meta_alt'].forEach(k => {
      if (g[k] && (!seo[k] || String(seo[k]).trim().length === 0)) {
        this.panel.seo[k] = g[k];
        changed++;
      }
    });
    if (changed === 0) {
      toast.info('이미 모든 필드에 값이 입력되어 있습니다. (기존 값은 덮어쓰지 않습니다)');
      return;
    }
    this.panel.isDirty = true;
    const reviewCount = data.reviewCount || 0;
    const tag = reviewCount > 0 ? '리뷰 데이터' : '상품명';
    toast.success(`${changed}개 필드를 ${tag}로 채웠습니다. 저장 버튼을 눌러야 반영됩니다.`);
  };

  @action
  closePanel = () => {
    if (this.panel.isDirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 닫으시겠습니까?')) return;
    this.panel.open = false;
    this.panel.productIndex = -1;
    this.panel.isDirty = false;
  };

  @action
  navPanel = (delta) => {
    if (this.panel.isDirty && !window.confirm('저장하지 않은 변경사항이 있습니다. 이동하시겠습니까?')) return;
    const next = this.panel.productIndex + delta;
    if (next < 0 || next >= this.filteredDocs.length) return;
    this.openPanel(next);
  };

  @action
  onPanelChange = (name, value) => {
    this.panel.seo[name] = value;
    this.panel.isDirty = true;
  };

  @action
  autoFillFromName = () => {
    const product = this.currentPanelProduct;
    if (!product) return;
    if (!isFilled(SEO_FIELDS[0], this.panel.seo.meta_title))   { this.panel.seo.meta_title = product.productName || ''; this.panel.isDirty = true; }
    if (!isFilled(SEO_FIELDS[4], this.panel.seo.meta_alt))     { this.panel.seo.meta_alt = product.productName || '';   this.panel.isDirty = true; }
    toast.info('상품명으로 비어있는 타이틀/Alt를 채웠습니다. 저장하면 반영됩니다.');
  };

  @computed
  get currentPanelProduct() {
    const idx = this.panel.productIndex;
    const filtered = this.filteredDocs;
    if (idx < 0 || idx >= filtered.length) return null;
    return filtered[idx];
  }

  @action
  savePanel = () => {
    const p = this.currentPanelProduct;
    if (!p) return;
    if (!this.isPaidUser) {
      toast.warn('상품 SEO 편집·저장은 유료 플랜 전용 기능입니다.');
      return;
    }
    this.panel.isSaving = true;
    const param = {
      store_id: this.props.me.store._id,
      shopNo: this.props.me.selShopNo || 1,
      product_no: p.platformProductId,
      seo: JSON.parse(JSON.stringify(this.panel.seo || {}))
    };
    this.props.updateDoc('updateCafe24ProductSeo', param, (res, errors) => {
      this.panel.isSaving = false;
      if (errors && errors.length > 0) {
        toast.error(errors[0].message || '저장 실패');
        return;
      }
      if (!res) { toast.error('저장 응답이 없습니다.'); return; }
      toast.success('상품 SEO가 저장되었습니다.');
      this.seoCache[p.platformProductId] = JSON.parse(JSON.stringify(this.panel.seo || {}));
      this.panel.isDirty = false;
    });
  };

  // ============ 일괄 설정 ============

  @action openBulk = () => {
    if (!this.isPaidUser) {
      toast.warn('일괄 설정은 유료 플랜 전용 기능입니다.');
      return;
    }
    this.bulkModal.open = true;
  };
  @action setBulkScope = (scope) => { this.bulkModal.scope = scope; };
  @action setBulkMode  = (mode)  => { this.bulkModal.mode = mode; };

  // 일괄 모달 프리셋 적용
  @action applyBulkPreset = (key) => {
    const m = this.bulkModal;
    // 모든 필드 일단 OFF
    Object.keys(m.enabled).forEach(k => { m.enabled[k] = false; });
    // source 초기화 (리뷰 기반이 아닌 경우)
    m.source = 'template';

    if (key === 'autoFromName') {
      m.enabled.meta_title = true;
      m.enabled.meta_alt = true;
      m.template.meta_title = '{상품명}';
      m.template.meta_alt = '{상품명}';
      m.mode = 'fillEmpty';
    } else if (key === 'exposeOn') {
      m.enabled.search_engine_exposure = true;
      m.template.search_engine_exposure = 'T';
      m.mode = 'overwrite';
    } else if (key === 'altOnly') {
      m.enabled.meta_alt = true;
      m.template.meta_alt = '{상품명}';
      m.mode = 'fillEmpty';
    } else if (key === 'reviewBased') {
      // 리뷰 데이터 기반: title, description, keywords, alt를 백엔드 룰로 채움
      m.source = 'reviewBased';
      m.enabled.meta_title = true;
      m.enabled.meta_description = true;
      m.enabled.meta_keywords = true;
      m.enabled.meta_alt = true;
      m.mode = 'fillEmpty';
    } else if (key === 'reset') {
      m.enabled = {meta_title: true, meta_description: false, meta_keywords: false, meta_author: false, meta_alt: true, search_engine_exposure: false};
      m.template = {meta_title: '{상품명}', meta_description: '', meta_keywords: '', meta_author: '', meta_alt: '{상품명}', search_engine_exposure: 'T'};
      m.mode = 'fillEmpty';
    }
  };

  @action setReviewMinRating = (rating) => { this.bulkModal.reviewMinRating = Number(rating); };
  @action toggleReviewPrepend = () => { this.bulkModal.reviewPrependProductName = !this.bulkModal.reviewPrependProductName; };
  @action closeBulk = () => { this.bulkModal.open = false; };
  @action toggleBulkField = (field) => { this.bulkModal.enabled[field] = !this.bulkModal.enabled[field]; };
  @action onBulkTemplateChange = (field, value) => { this.bulkModal.template[field] = value; };

  applyTemplate = (tpl, product) => {
    if (typeof tpl !== 'string') return tpl;
    return tpl
      .replace(/\{상품명\}/g, product.productName || '')
      .replace(/\{상품번호\}/g, String(product.platformProductId || ''));
  };

  getBulkTargets = async () => {
    const {scope} = this.bulkModal;
    if (scope === 'page') {
      return this.filteredDocs.slice();
    }
    if (scope === 'selected') {
      // 선택 항목 조회: allProducts + list.docs 합집합에서 매칭
      const map = new Map();
      (this.allProducts || []).forEach(p => map.set(p.platformProductId, p));
      ((this.list && this.list.docs) || []).forEach(p => map.set(p.platformProductId, p));
      return this.selectedIds.map(id => map.get(id)).filter(Boolean);
    }
    // all: 모든 페이지 누적
    const targets = [];
    const limit = 100;
    let page = 1, totalPages = 1;
    do {
      const params = {
        store_id: this.props.me.store._id,
        shopNo: this.props.me.selShopNo || 1,
        page, limit, searchParams: {keyword: ''}
      };
      const res = await this.props.getProductList(params);
      if (!res || !res.docs) break;
      targets.push(...res.docs);
      totalPages = res.totalPages || 1;
      page++;
      if (page > totalPages) break;
    } while (true);
    return targets;
  };

  // 단건 처리(processOne) / 단건 페이로드 생성(buildSeoPayload)은 backend batch로 통합되어 제거됨.
  // 일괄 설정은 startBulk → updateCafe24ProductSeoBatch (backend chunk + sleep) 한 경로로 처리.

  @action startBulk = async () => {
    if (!this.props.me || !this.props.me.store) return;
    if (!this.isPaidUser) {
      toast.warn('일괄 설정은 유료 플랜 전용 기능입니다.');
      return;
    }
    if (!Object.values(this.bulkModal.enabled).some(Boolean)) {
      toast.error('적용할 필드를 1개 이상 선택하세요.');
      return;
    }
    if (this.bulkModal.scope === 'selected' && this.selectedIds.length === 0) {
      toast.error('선택한 상품이 없습니다.');
      return;
    }
    this.closeBulk();
    this.bulkProgress = {open: true, total: 0, done: 0, success: 0, failed: 0, failedList: [], isRunning: true, isCancelling: false, isCompleted: false};
    // optimistic tick 시작 (시각 진행률 가속)
    this.startBulkOptimisticTick();
    let targets = [];
    try { targets = await this.getBulkTargets(); }
    catch (e) {
      toast.error('대상 상품 조회 실패');
      this.bulkProgress.isRunning = false;
      this.stopBulkOptimisticTick();
      return;
    }
    this.bulkProgress.total = targets.length;
    if (targets.length === 0) {
      toast.warn('적용 대상 없음');
      this.bulkProgress.isRunning = false;
      this.bulkProgress.isCompleted = true;
      this.stopBulkOptimisticTick();
      return;
    }

    // batch 공통 파라미터 (frontend bulk 설정 → backend 전달)
    const m = this.bulkModal;
    const batchParams = {
      store_id: this.props.me.store._id,
      shopNo: this.props.me.selShopNo || 1,
      mode: m.source === 'reviewBased' ? 'reviewBased' : 'template',
      fieldMode: m.mode,
      enabled: JSON.parse(JSON.stringify(m.enabled || {})),
    };
    if (m.source === 'reviewBased') {
      batchParams.review = {
        minRating: m.reviewMinRating || 4,
        prependProductName: m.reviewPrependProductName !== false
      };
    } else {
      batchParams.template = JSON.parse(JSON.stringify(m.template || {}));
    }

    // 프론트 batch 크기 (재분석과 동일하게 ANALYSIS_BATCH_SIZE 재사용)
    const FRONT_CHUNK = this.ANALYSIS_BATCH_SIZE;
    for (let i = 0; i < targets.length; i += FRONT_CHUNK) {
      if (this.bulkProgress.isCancelling) break;
      const slice = targets.slice(i, i + FRONT_CHUNK);
      const items = slice.map(p => ({
        product_no: p.platformProductId,
        productName: p.productName || ''
      }));

      // updateDoc은 callback 시그니처(result, errors)를 사용 — Promise wrapping
      // GraphQL Response shape: result = {data: [...]} → 안의 array를 꺼내야 함
      const res = await new Promise((resolve) => {
        this.props.updateDoc('updateCafe24ProductSeoBatch', {...batchParams, items}, (result, errors) => {
          if (errors && errors.length > 0) {
            resolve({error: errors[0].message || 'GraphQL error'});
          } else {
            const list = result && Array.isArray(result.data) ? result.data : [];
            resolve({data: list});
          }
        });
      });

      if (res.error) {
        // batch 전체 실패
        slice.forEach(p => {
          this.bulkProgress.failed++;
          this.bulkProgress.failedList.push({name: p.productName || p.platformProductId, error: res.error});
        });
      } else {
        // 응답 항목별 카운트 (응답 순서가 items 순서와 일치)
        const results = res.data;
        results.forEach((r, idx) => {
          const p = slice[idx];
          if (r.success) {
            this.bulkProgress.success++;
            // 캐시 갱신 (PUT이 성공하면 backend가 ProductSeoCache 갱신 — frontend seoCache는 다음 분석/조회 때 동기화)
          } else {
            this.bulkProgress.failed++;
            this.bulkProgress.failedList.push({name: p.productName || p.platformProductId, error: r.error || '오류'});
          }
        });
      }
      this.bulkProgress.done = Math.min(targets.length, i + FRONT_CHUNK);
    }

    this.bulkProgress.isRunning = false;
    this.bulkProgress.isCompleted = true;
    // optimistic을 실제값으로 동기화 후 정지
    this.bulkOptimisticDone = this.bulkProgress.done;
    this.stopBulkOptimisticTick();
    if (this.bulkProgress.failed === 0 && this.bulkProgress.success > 0) toast.success(`${this.bulkProgress.success}개 상품에 적용`);
    else if (this.bulkProgress.failed > 0) toast.warn(`성공 ${this.bulkProgress.success} / 실패 ${this.bulkProgress.failed}`);
  };

  @action cancelBulk = () => { this.bulkProgress.isCancelling = true; };
  @action closeBulkProgress = () => { this.bulkProgress.open = false; };

  @action goPage = (page) => {
    const max = this.effectiveTotalPages;
    if (page < 1 || page > max) return;
    this.listParams.page = page;
    if (!this.isClientFilterMode) this.loadList();
  };

  @action setStatusFilter = (key) => {
    if (this.statusFilter === key) {
      this.statusFilter = 'all';
    } else {
      this.statusFilter = key;
    }
    this.listParams.page = 1;
    // 필터 해제시 서버 데이터 재로드
    if (this.statusFilter === 'all') this.loadList();
  };

  @action onSearch = () => { this.listParams.page = 1; this.loadList(); };

  // ============ 렌더링 ============

  renderLoaderSvg = () => (
    <div className="seopl-loader">
      <svg viewBox="0 0 100 100" className="seopl-loader__svg" width="96" height="96">
        <defs>
          <linearGradient id="seoplGradMain" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"  stopColor="#5664D2"/>
            <stop offset="50%" stopColor="#7C3AED"/>
            <stop offset="100%" stopColor="#A78BFA"/>
          </linearGradient>
          <linearGradient id="seoplGradFade" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%"   stopColor="#5664D2" stopOpacity="0"/>
            <stop offset="100%" stopColor="#5664D2" stopOpacity="0.6"/>
          </linearGradient>
          <radialGradient id="seoplGradCenter" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#C4B5FD"/>
            <stop offset="60%" stopColor="#7C3AED"/>
            <stop offset="100%" stopColor="#5664D2"/>
          </radialGradient>
        </defs>
        <circle cx="50" cy="50" r="46" fill="none" stroke="url(#seoplGradFade)" strokeWidth="1.5" strokeDasharray="3 6" opacity="0.7" className="seopl-loader__ring1"/>
        <circle cx="50" cy="50" r="38" fill="none" stroke="url(#seoplGradMain)" strokeWidth="3.5" strokeDasharray="180 240" strokeLinecap="round" className="seopl-loader__ring2"/>
        <circle cx="50" cy="50" r="28" fill="none" stroke="url(#seoplGradMain)" strokeWidth="1.5" strokeDasharray="80 130" strokeLinecap="round" opacity="0.5" className="seopl-loader__ring3"/>
        <circle cx="50" cy="50" r="6" fill="url(#seoplGradCenter)" className="seopl-loader__core"/>
        <g className="seopl-loader__orbit">
          <circle cx="50" cy="12"  r="2.6" fill="#A78BFA"/>
          <circle cx="83" cy="69"  r="2.6" fill="#7C3AED"/>
          <circle cx="17" cy="69"  r="2.6" fill="#5664D2"/>
        </g>
      </svg>
    </div>
  );

  renderAnalysisOverlay = () => {
    const a = this.analysis;
    if (!a.isRunning || a.mode !== 'cafe24') return null;
    const total = a.total || 0;
    // 페이즈 1 (상품 로드) — 실제값 그대로, 분석은 0
    // 페이즈 2 (카페24 페치) — optimisticAnalyzed (부드러운 가속, 응답 시 보정)
    const inFetchPhase = a.productsLoaded >= total && total > 0;
    const displayed = inFetchPhase
      ? Math.floor(this.optimisticAnalyzed)
      : 0;
    const pct = total > 0 ? Math.min(100, Math.round((displayed / total) * 100)) : 0;
    const phaseLabel = inFetchPhase
      ? '카페24에서 SEO 데이터 수집 중'
      : '상품 목록 수집 중';

    return (
      <div className="seopl-overlay" role="dialog" aria-modal="true" aria-labelledby="seopl-overlay-title">
        <div className="seopl-overlay__backdrop"/>
        <div className="seopl-overlay__card">
          {this.renderLoaderSvg()}

          <h3 id="seopl-overlay-title" className="seopl-overlay__title">AI 분석 중</h3>
          <div className="seopl-overlay__phase">{phaseLabel}</div>

          <div className="seopl-overlay__count">
            <span className="seopl-overlay__num">{displayed.toLocaleString()}</span>
            <span className="seopl-overlay__denom"> / {total.toLocaleString()}개</span>
          </div>

          <div className="seopl-overlay__progress">
            <div className="seopl-overlay__progress-fill" style={{width: pct + '%'}}/>
          </div>
          <div className="seopl-overlay__pct">{pct}%</div>

          <Button theme="light" size="sm"
                  className="seopl-overlay__cancel"
                  disabled={a.isCancelling}
                  onClick={this.cancelAnalysis}>
            {a.isCancelling ? '중지 중...' : '중지'}
          </Button>
        </div>
      </div>
    );
  };

  renderHealthCard = () => {
    const s = this.healthStats;
    const a = this.analysis;
    const hasData = s.analyzed > 0;
    const pct = hasData ? s.avgCompletion : 0;
    const color = pct >= 90 ? '#15803D' : pct >= 70 ? '#CA8A04' : pct >= 50 ? '#EA580C' : '#DC2626';
    const label = pct >= 90 ? '우수' : pct >= 70 ? '양호' : pct >= 50 ? '주의' : '개선 필요';

    // 분석 진행률 (상품 로드 + SEO 페치 합산)
    const denom = (s.total || 0) * 2; // 상품 로드 1점 + SEO 분석 1점
    const numer = (a.productsLoaded || 0) + (a.analyzed || 0);
    const progressPct = denom > 0 ? Math.min(100, Math.round((numer / denom) * 100)) : 0;

    // SVG 도넛 차트 — 백그라운드 트랙 + 진행률 호
    const SIZE = 110;
    const STROKE = 9;
    const R = (SIZE - STROKE) / 2; // 반지름
    const CIRC = 2 * Math.PI * R;  // 원주
    const arcOffset = CIRC * (1 - Math.max(0, Math.min(100, pct)) / 100);

    return (
      <div className="seopl-health">
        <div className="seopl-health__score">
          <svg width={SIZE} height={SIZE} className="seopl-health__ring" aria-hidden="true">
            <circle className="seopl-health__ring-track"
                    cx={SIZE/2} cy={SIZE/2} r={R}
                    fill="none" strokeWidth={STROKE}/>
            <circle className="seopl-health__ring-fill"
                    cx={SIZE/2} cy={SIZE/2} r={R}
                    fill="none" strokeWidth={STROKE}
                    stroke={color}
                    strokeDasharray={CIRC}
                    strokeDashoffset={arcOffset}
                    strokeLinecap="round"
                    transform={`rotate(-90 ${SIZE/2} ${SIZE/2})`}/>
          </svg>
          <div className="seopl-health__score-text">
            <div className="seopl-health__pct" style={{color}}>{pct}<span>%</span></div>
            <div className="seopl-health__label" style={{color}}>{label}</div>
          </div>
        </div>
        <div className="seopl-health__divider"/>
        <div className="seopl-health__main">
          <div className="seopl-health__topline">
            <div className="seopl-health__title">전체 카탈로그 SEO 현황</div>
            <div className="seopl-health__status">
              {a.isRunning ? (
                <>
                  <span className="seopl-health__dot seopl-health__dot--run"/>
                  <span>{a.mode === 'cafe24' ? '카페24 재분석 중' : '캐시 로드 중'} · {s.analyzed.toLocaleString()} / {s.total.toLocaleString()}개 ({progressPct}%)</span>
                  <button type="button" className="seopl-health__cancel" onClick={this.cancelAnalysis}>중지</button>
                </>
              ) : a.isComplete ? (
                <>
                  <span className="seopl-health__dot seopl-health__dot--ok"/>
                  <span>
                    전체 {s.total.toLocaleString()}개
                    {a.lastSyncedAt && ` · 마지막 동기화 ${this.formatRelativeTime(a.lastSyncedAt)}`}
                  </span>
                  <button type="button" className="seopl-health__refresh" onClick={this.rerunAnalysis} title="카페24에서 최신 데이터로 재분석">
                    <FeatherIcon icon="refresh-cw" size="13"/> 재분석
                  </button>
                </>
              ) : a.isCancelling ? (
                <span>중지됨 · {s.analyzed.toLocaleString()} / {s.total.toLocaleString()}개</span>
              ) : (
                <>
                  <span className="seopl-health__dot seopl-health__dot--idle"/>
                  <span>{s.analyzed > 0 ? `${s.analyzed.toLocaleString()} / ${s.total.toLocaleString()}개` : '분석 대기'}</span>
                  <button type="button" className="seopl-health__refresh" onClick={this.rerunAnalysis} title="카페24에서 최신 데이터로 재분석">
                    <FeatherIcon icon="refresh-cw" size="13"/> 재분석
                  </button>
                </>
              )}
            </div>
          </div>
          {a.isRunning && (
            <div className="seopl-health__progress">
              <div className="seopl-health__progress-fill" style={{width: progressPct + '%'}}/>
            </div>
          )}
          <div className="seopl-health__tiles">
            <button className="seopl-tile" type="button" onClick={() => this.setStatusFilter('all')}>
              <div className="seopl-tile__num">{s.complete.toLocaleString()}</div>
              <div className="seopl-tile__label">완료 (100%)</div>
            </button>
            <button className={`seopl-tile seopl-tile--warn${this.statusFilter === 'missingTitle' ? ' is-active' : ''}`} type="button"
                    onClick={() => this.setStatusFilter('missingTitle')}>
              <div className="seopl-tile__num">{s.missing.meta_title.toLocaleString()}</div>
              <div className="seopl-tile__label">타이틀 누락</div>
            </button>
            <button className={`seopl-tile seopl-tile--warn${this.statusFilter === 'missingDesc' ? ' is-active' : ''}`} type="button"
                    onClick={() => this.setStatusFilter('missingDesc')}>
              <div className="seopl-tile__num">{s.missing.meta_description.toLocaleString()}</div>
              <div className="seopl-tile__label">설명 누락</div>
            </button>
            <button className={`seopl-tile seopl-tile--warn${this.statusFilter === 'missingAlt' ? ' is-active' : ''}`} type="button"
                    onClick={() => this.setStatusFilter('missingAlt')}>
              <div className="seopl-tile__num">{s.missing.meta_alt.toLocaleString()}</div>
              <div className="seopl-tile__label">Alt 누락</div>
            </button>
            <button className={`seopl-tile seopl-tile--err${this.statusFilter === 'hidden' ? ' is-active' : ''}`} type="button"
                    onClick={() => this.setStatusFilter('hidden')}>
              <div className="seopl-tile__num">{s.hidden.toLocaleString()}</div>
              <div className="seopl-tile__label">검색노출 OFF</div>
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderFilterBar = () => {
    const total = this.list && this.list.totalDocs ? this.list.totalDocs : 0;

    const chips = [
      {key: 'all',           label: '전체'},
      {key: 'incomplete',    label: '미완성 항목 있음'},
      {key: 'missingTitle',  label: '타이틀 누락'},
      {key: 'missingDesc',   label: '설명 누락'},
      {key: 'missingAlt',    label: 'Alt 누락'},
      {key: 'hidden',        label: '검색노출 OFF'}
    ];

    return (
      <div className="seopl-filterbar">
        <div className="seopl-filterbar__chips">
          {chips.map(c => (
            <button key={c.key} type="button"
                    className={`seopl-chip${this.statusFilter === c.key ? ' is-active' : ''}`}
                    onClick={() => this.setStatusFilter(c.key)}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="seopl-filterbar__right">
          <div className="seopl-search">
            <FeatherIcon icon="search" size="14" className="seopl-search__icon"/>
            <input type="text" placeholder="상품명 검색"
                   value={this.keyword}
                   onChange={(e) => { this.keyword = e.target.value; }}
                   onKeyDown={(e) => { if (e.key === 'Enter') this.onSearch(); }}
                   disabled={this.isClientFilterMode}/>
          </div>
          <span className="seopl-meta">
            {this.isClientFilterMode
              ? `${this.effectiveTotal.toLocaleString()}개 일치 (전체 ${total.toLocaleString()}개 중)`
              : `${total.toLocaleString()}개`}
          </span>
        </div>
      </div>
    );
  };

  renderStatusDots = (seo) => {
    if (seo === undefined) {
      return <div className="seopl-dots seopl-dots--loading"><FeatherIcon icon="loader" size="12" className="seopl-spin"/></div>;
    }
    const tooltipFor = (f, value, ok) => {
      if (f.key === 'search_engine_exposure') {
        return `${f.label}: ${ok ? '노출함' : '노출안함'}`;
      }
      return `${f.label}: ${ok ? '설정됨' : '비어있음'}`;
    };
    return (
      <div className="seopl-dots">
        {SEO_FIELDS.map(f => {
          const ok = isFilled(f, seo[f.key]);
          return (
            <span key={f.key}
                  className={`seopl-dot${ok ? ' is-ok' : ' is-empty'}`}
                  title={tooltipFor(f, seo[f.key], ok)}>
              {f.short}
            </span>
          );
        })}
      </div>
    );
  };

  renderSelectionBar = () => {
    const n = this.selectedIds.length;
    if (n === 0) return null;
    return (
      <div className="seopl-selection">
        <div className="seopl-selection__info">
          <FeatherIcon icon="check-circle" size="14"/>
          <strong>{n}개</strong> 상품 선택됨
        </div>
        <div className="seopl-selection__actions">
          <Button size="sm" theme="primary"
                  onClick={() => { this.bulkModal.scope = 'selected'; this.openBulk(); }}
                  disabled={!this.isPaidUser}
                  title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
            <FeatherIcon icon="zap" size="13"/> 선택 항목 일괄 설정
          </Button>
          <Button size="sm" theme="light" onClick={this.clearSelection}>
            <FeatherIcon icon="x" size="13"/> 선택 해제
          </Button>
        </div>
      </div>
    );
  };

  renderTable = () => {
    const docs = this.filteredDocs;
    if (this.isLoading) {
      return <div className="seopl-loading"><FeatherIcon icon="loader" size="20" className="seopl-spin"/><span>상품 목록 불러오는 중...</span></div>;
    }
    if (docs.length === 0) {
      return (
        <div className="seopl-empty">
          <FeatherIcon icon="inbox" size="32"/>
          <p>{this.statusFilter !== 'all' ? '필터 조건에 맞는 상품이 없습니다.' : '표시할 상품이 없습니다.'}</p>
          {this.statusFilter !== 'all' && (
            <Button size="sm" theme="light" onClick={() => { this.statusFilter = 'all'; }}>필터 초기화</Button>
          )}
        </div>
      );
    }
    return (
      <div className="seopl-table-wrap">
        <table className="seopl-table">
          <thead>
            <tr>
              <th style={{width: 36}}>
                <input type="checkbox"
                       checked={this.isAllVisibleSelected}
                       onChange={this.toggleSelectAll}/>
              </th>
              <th style={{width: 64}}/>
              <th>상품</th>
              <th style={{width: 280}}>SEO 항목 상태</th>
              <th style={{width: 92}}>완성도</th>
              <th style={{width: 100, textAlign: 'right'}}/>
            </tr>
          </thead>
          <tbody>
            {docs.map((p, idx) => {
              const seo = this.seoCache[p.platformProductId];
              const pct = seo !== undefined ? getCompletion(seo) : null;
              const checked = this.selectedIds.includes(p.platformProductId);
              return (
                <tr key={p._id || p.platformProductId}
                    className={`seopl-row${checked ? ' is-selected' : ''}`}
                    onClick={() => this.openPanel(idx)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" checked={checked}
                           onChange={() => this.toggleSelect(p.platformProductId)}/>
                  </td>
                  <td>
                    {p.images && p.images.s_1 ? (
                      <img src={p.images.s_1} alt="" className="seopl-thumb"/>
                    ) : (
                      <div className="seopl-thumb seopl-thumb--placeholder"><FeatherIcon icon="image" size="16"/></div>
                    )}
                  </td>
                  <td>
                    <div className="seopl-pname">{p.productName || '(이름 없음)'}</div>
                    <div className="seopl-pno">상품번호 {p.platformProductId}</div>
                  </td>
                  <td>{this.renderStatusDots(seo)}</td>
                  <td>
                    {pct === null ? <span className="seopl-pct seopl-pct--loading">—</span>
                      : <div className={`seopl-pct seopl-pct--${pct >= 90 ? 'ok' : pct >= 50 ? 'warn' : 'err'}`}>
                          <div className="seopl-pct__bar"><div className="seopl-pct__fill" style={{width: pct + '%'}}/></div>
                          <span>{pct}%</span>
                        </div>}
                  </td>
                  <td style={{textAlign: 'right'}} onClick={(e) => e.stopPropagation()}>
                    <Button size="sm" theme="light" onClick={() => this.openPanel(idx)}>
                      <FeatherIcon icon="edit-2" size="12"/> 편집
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  renderStarBar = (rating) => {
    const r = Math.max(0, Math.min(5, Number(rating) || 0));
    const full = Math.floor(r);
    const half = (r - full) >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    return (
      <span className="seopl-stars" aria-label={`${r}점`}>
        {Array.from({length: full}).map((_, i) => <span key={'f'+i} className="seopl-star is-on">★</span>)}
        {half && <span className="seopl-star is-half">★</span>}
        {Array.from({length: empty}).map((_, i) => <span key={'e'+i} className="seopl-star">★</span>)}
      </span>
    );
  };

  renderInsightSection = () => {
    const ins = this.panelInsight;
    return (
      <div className="seopl-insight">
        <div className="seopl-insight__head">
          <div className="seopl-insight__title">
            <FeatherIcon icon="message-square" size="14"/>
            <span>리뷰 인사이트</span>
          </div>
          <div className="seopl-insight__rating-select">
            <label>분석 기준</label>
            <FormSelect value={ins.minRating}
                        onChange={(e) => this.setInsightMinRating(e.target.value)}>
              <option value={5}>5점만</option>
              <option value={4}>4점 이상 (기본)</option>
              <option value={3}>3점 이상</option>
              <option value={1}>전체</option>
            </FormSelect>
          </div>
        </div>

        <div className="seopl-insight__opts">
          <label className="seopl-insight__check">
            <input type="checkbox"
                   checked={ins.prependProductName !== false}
                   onChange={this.togglePanelPrepend}/>
            <span>상품명을 앞에 표기 <em>(기본 ON · 검색엔진 가중치 ↑)</em></span>
          </label>
        </div>

        {ins.isLoading ? (
          <div className="seopl-insight__loading">
            <FeatherIcon icon="loader" size="14" className="seopl-spin"/>
            <span>리뷰 데이터 분석 중...</span>
          </div>
        ) : !ins.data ? (
          <div className="seopl-insight__empty">
            <FeatherIcon icon="info" size="14"/>
            <span>분석할 리뷰 데이터가 없습니다.</span>
          </div>
        ) : ins.data.reviewCount === 0 ? (
          <>
            <div className="seopl-insight__empty">
              <FeatherIcon icon="info" size="14"/>
              <span>선택한 별점 조건에 해당하는 리뷰가 없습니다. 상품명만으로 SEO를 채울 수 있습니다.</span>
            </div>
            <div className="seopl-insight__actions">
              <Button size="sm" theme="primary"
                      onClick={this.applyInsightToForm}
                      disabled={!this.isPaidUser}
                      title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
                <FeatherIcon icon="zap" size="12"/> 상품명으로 비어있는 필드 채우기
              </Button>
              <span className="seopl-insight__hint">기존 값은 덮어쓰지 않습니다</span>
            </div>
          </>
        ) : (
          <>
            <div className="seopl-insight__summary">
              {this.renderStarBar(ins.data.avgRating)}
              <strong>{ins.data.avgRating}</strong>
              <span className="seopl-insight__count">({ins.data.reviewCount.toLocaleString()}건)</span>
            </div>

            {ins.data.topKeywords && ins.data.topKeywords.length > 0 && (
              <div className="seopl-insight__block">
                <div className="seopl-insight__label">자주 언급된 키워드</div>
                <div className="seopl-insight__chips">
                  {ins.data.topKeywords.slice(0, 8).map((kw, i) => (
                    <span key={i} className="seopl-insight__chip">{kw.term}<em>{kw.freq}</em></span>
                  ))}
                </div>
              </div>
            )}

            {ins.data.bestSnippets && ins.data.bestSnippets.length > 0 && (
              <div className="seopl-insight__block">
                <div className="seopl-insight__label">베스트 후기</div>
                <div className="seopl-insight__snippets">
                  {ins.data.bestSnippets.slice(0, 3).map((sn, i) => (
                    <blockquote key={i} className="seopl-insight__snippet">"{sn.text}"</blockquote>
                  ))}
                </div>
              </div>
            )}

            <div className="seopl-insight__actions">
              <Button size="sm" theme="primary"
                      onClick={this.applyInsightToForm}
                      disabled={!this.isPaidUser}
                      title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
                <FeatherIcon icon="zap" size="12"/> 이 데이터로 비어있는 필드 채우기
              </Button>
              <span className="seopl-insight__hint">기존 값은 덮어쓰지 않습니다</span>
            </div>
          </>
        )}
      </div>
    );
  };

  renderSerpPreview = () => {
    const p = this.currentPanelProduct;
    if (!p) return null;
    const seo = this.panel.seo || {};
    const title = seo.meta_title || p.productName || '제목 없음';
    const desc = seo.meta_description || '검색 결과에 표시될 설명을 입력하세요.';
    const url = `example-shop.com › product › ${p.platformProductId || ''}`;
    return (
      <div className="seopl-serp">
        <div className="seopl-serp__head">검색 결과 미리보기</div>
        <div className="seopl-serp__url">{url}</div>
        <div className="seopl-serp__title">{title}</div>
        <div className="seopl-serp__desc">{desc}</div>
      </div>
    );
  };

  renderPanelField = (field) => {
    const seo = this.panel.seo || {};
    const value = seo[field.key] != null ? seo[field.key] : '';
    const len = (value || '').length;
    const showCounter = field.max > 0;
    const inRange = !showCounter || (len >= (field.min || 0) && len <= field.max);

    return (
      <div className="seopl-pf" key={field.key}>
        <div className="seopl-pf__head">
          <label>{field.label}</label>
          {showCounter && (
            <span className={`seopl-pf__count${inRange ? '' : ' is-out'}`}>
              {len}{field.max ? ` / ${field.max}` : ''}자
              {!inRange && len < (field.min || 0) ? ` · ${field.min}자 이상 권장` : ''}
            </span>
          )}
        </div>
        {field.type === 'textarea' ? (
          <FormTextarea rows={field.rows || 3} value={value || ''} disabled={!this.isPaidUser}
                        onChange={(e) => this.onPanelChange(field.key, e.target.value)}/>
        ) : field.type === 'select' ? (
          <FormSelect value={value || 'T'} disabled={!this.isPaidUser} onChange={(e) => this.onPanelChange(field.key, e.target.value)}>
            {SE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </FormSelect>
        ) : (
          <FormInput value={value || ''} disabled={!this.isPaidUser} onChange={(e) => this.onPanelChange(field.key, e.target.value)}/>
        )}
        {field.helper && <div className="seopl-pf__helper">{field.helper}</div>}
      </div>
    );
  };

  renderPanel = () => {
    const open = this.panel.open;
    const p = this.currentPanelProduct;
    if (!open) return null;
    const total = this.filteredDocs.length;
    const idx = this.panel.productIndex;

    return (
      <>
        <div className="seopl-panel-backdrop" onClick={this.closePanel}/>
        <aside className={`seopl-panel${open ? ' is-open' : ''}`}>
          <header className="seopl-panel__head">
            <div className="seopl-panel__title-wrap">
              <div className="seopl-panel__title">상품 SEO 편집</div>
              <div className="seopl-panel__nav">
                <button type="button" className="seopl-panel__nav-btn" disabled={idx <= 0} onClick={() => this.navPanel(-1)}>
                  <FeatherIcon icon="chevron-left" size="14"/>
                </button>
                <span>{idx + 1} / {total}</span>
                <button type="button" className="seopl-panel__nav-btn" disabled={idx >= total - 1} onClick={() => this.navPanel(1)}>
                  <FeatherIcon icon="chevron-right" size="14"/>
                </button>
              </div>
            </div>
            <button type="button" className="seopl-panel__close" onClick={this.closePanel}>
              <FeatherIcon icon="x" size="18"/>
            </button>
          </header>

          {!p ? null : (
            <>
              <div className="seopl-panel__product">
                {p.images && p.images.s_1
                  ? <img src={p.images.s_1} alt=""/>
                  : <div className="seopl-thumb seopl-thumb--placeholder"><FeatherIcon icon="image" size="20"/></div>}
                <div>
                  <div className="seopl-panel__pname">{p.productName}</div>
                  <div className="seopl-panel__pno">상품번호 {p.platformProductId}</div>
                </div>
              </div>

              <div className="seopl-panel__quick">
                <Button size="sm" theme="light"
                        onClick={this.autoFillFromName}
                        disabled={!this.isPaidUser}
                        title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
                  <FeatherIcon icon="zap" size="12"/> 상품명으로 자동 채우기
                </Button>
              </div>

              <div className="seopl-panel__body">
                {this.panel.isLoading ? (
                  <div className="seopl-loading"><FeatherIcon icon="loader" size="20" className="seopl-spin"/><span>SEO 조회 중...</span></div>
                ) : (
                  <>
                    {this.renderInsightSection()}
                    {this.renderSerpPreview()}
                    {SEO_FIELDS.map(f => this.renderPanelField(f))}
                  </>
                )}
              </div>

              <footer className="seopl-panel__foot">
                <span className="seopl-panel__dirty">{this.panel.isDirty ? <><FeatherIcon icon="circle" size="8" className="seopl-dirty-dot"/> 저장되지 않은 변경사항</> : ''}</span>
                <div>
                  <Button theme="secondary" onClick={this.closePanel}>닫기</Button>
                  <Button theme="primary"
                          onClick={this.savePanel}
                          disabled={this.panel.isLoading || this.panel.isSaving || !this.panel.isDirty || !this.isPaidUser}
                          title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
                    <FeatherIcon icon="save" size="14"/> {this.panel.isSaving ? '저장 중...' : '저장'}
                  </Button>
                </div>
              </footer>
            </>
          )}
        </aside>
      </>
    );
  };

  renderBulkModal = () => {
    if (!this.bulkModal.open) return null;
    const m = this.bulkModal;
    const fieldDefs = [
      {key: 'meta_title',          label: '브라우저 타이틀',     placeholder: '예: {상품명} | 쇼핑몰명',           type: 'text'},
      {key: 'meta_description',    label: '메타 설명',           placeholder: '예: {상품명} 상세 설명...',        type: 'textarea'},
      {key: 'meta_keywords',       label: '키워드',              placeholder: '예: 의류, 데일리룩, {상품명}',     type: 'text'},
      {key: 'meta_author',         label: 'Author',              placeholder: '예: 쇼핑몰명',                     type: 'text'},
      {key: 'meta_alt',            label: '이미지 Alt',          placeholder: '예: {상품명}',                     type: 'text'},
      {key: 'search_engine_exposure', label: '검색엔진 노출',     placeholder: '',                                type: 'select'}
    ];

    const docs = this.filteredDocs;
    const samples = docs.slice(0, 3);

    return (
      <Modal open={m.open} toggle={() => {}} size="lg" className="seopl-bulk-modal">
        <ModalHeader toggle={this.closeBulk}>일괄 SEO 설정</ModalHeader>
        <ModalBody>
          <div className="seopl-bulk-info">
            <FeatherIcon icon="info" size="14"/>
            <span>변경사항은 카페24에 즉시 반영됩니다. 텍스트 필드에 <code>{'{상품명}'}</code>, <code>{'{상품번호}'}</code> 변수를 사용할 수 있습니다.</span>
          </div>

          <div className="seopl-bulk-presets">
            <span className="seopl-bulk-presets__label">빠른 프리셋</span>
            <button type="button"
                    className={`seopl-bulk-preset${m.source === 'reviewBased' ? ' is-active' : ''}`}
                    onClick={() => this.applyBulkPreset('reviewBased')}>
              <FeatherIcon icon="message-square" size="12"/> 리뷰 기반 자동 생성
            </button>
            <button type="button" className="seopl-bulk-preset" onClick={() => this.applyBulkPreset('autoFromName')}>
              <FeatherIcon icon="zap" size="12"/> 상품명 자동 채우기 (타이틀·Alt)
            </button>
            <button type="button" className="seopl-bulk-preset" onClick={() => this.applyBulkPreset('altOnly')}>
              <FeatherIcon icon="image" size="12"/> 이미지 Alt만 채우기
            </button>
            <button type="button" className="seopl-bulk-preset" onClick={() => this.applyBulkPreset('exposeOn')}>
              <FeatherIcon icon="eye" size="12"/> 검색노출 ON 일괄
            </button>
            <button type="button" className="seopl-bulk-preset seopl-bulk-preset--ghost" onClick={() => this.applyBulkPreset('reset')}>
              초기화
            </button>
          </div>

          {m.source === 'reviewBased' && (
            <div className="seopl-bulk-review-opts">
              <FeatherIcon icon="info" size="13"/>
              <span>리뷰 데이터를 분석해 타이틀·설명·키워드·Alt를 자동 생성합니다. 리뷰가 없는 상품은 상품명만으로 채워집니다.</span>
              <div className="seopl-bulk-review-opts__select">
                <label>분석 기준 별점</label>
                <FormSelect value={m.reviewMinRating}
                            onChange={(e) => this.setReviewMinRating(e.target.value)}>
                  <option value={5}>5점만</option>
                  <option value={4}>4점 이상 (기본)</option>
                  <option value={3}>3점 이상</option>
                  <option value={1}>전체</option>
                </FormSelect>
              </div>
              <label className="seopl-bulk-review-opts__check">
                <input type="checkbox"
                       checked={m.reviewPrependProductName !== false}
                       onChange={this.toggleReviewPrepend}/>
                <span>상품명을 앞에 표기 <em>(기본 ON)</em></span>
              </label>
            </div>
          )}

          <div className="seopl-bulk-row">
            <div className="seopl-bulk-row__label">적용 범위</div>
            <div className="seopl-bulk-row__value">
              <div className="seopl-pillgroup" role="radiogroup">
                <button type="button"
                        className={`seopl-pill${m.scope === 'all' ? ' is-active' : ''}`}
                        onClick={() => this.setBulkScope('all')}>
                  전체 상품
                </button>
                <button type="button"
                        className={`seopl-pill${m.scope === 'page' ? ' is-active' : ''}`}
                        onClick={() => this.setBulkScope('page')}>
                  현재 페이지 <em>({this.filteredDocs.length}개)</em>
                </button>
                <button type="button"
                        className={`seopl-pill${m.scope === 'selected' ? ' is-active' : ''}`}
                        disabled={this.selectedIds.length === 0}
                        onClick={() => this.setBulkScope('selected')}>
                  선택한 상품 <em>({this.selectedIds.length}개)</em>
                </button>
              </div>
            </div>
          </div>

          <div className="seopl-bulk-row">
            <div className="seopl-bulk-row__label">적용 방식</div>
            <div className="seopl-bulk-row__value">
              <div className="seopl-pillgroup" role="radiogroup">
                <button type="button"
                        className={`seopl-pill${m.mode === 'fillEmpty' ? ' is-active' : ''}`}
                        onClick={() => this.setBulkMode('fillEmpty')}>
                  비어있는 항목만 채움 <em>(기존 값 유지)</em>
                </button>
                <button type="button"
                        className={`seopl-pill${m.mode === 'overwrite' ? ' is-active' : ''}`}
                        onClick={() => this.setBulkMode('overwrite')}>
                  모든 값 덮어쓰기 <em>(기존 값 무시)</em>
                </button>
              </div>
            </div>
          </div>

          {m.source === 'reviewBased' ? (
            <div className="seopl-bulk-fields">
              <div className="seopl-bulk-fields__head">
                <span>적용</span><span>필드</span><span style={{textAlign: 'left'}}>설명</span>
              </div>
              {fieldDefs.filter(f => ['meta_title', 'meta_description', 'meta_keywords', 'meta_alt'].includes(f.key)).map(f => (
                <div key={f.key} className={`seopl-bulk-field${m.enabled[f.key] ? ' is-on' : ''}`}>
                  <div className="seopl-bulk-field__check"><input type="checkbox" checked={!!m.enabled[f.key]} onChange={() => this.toggleBulkField(f.key)}/></div>
                  <div className="seopl-bulk-field__label">{f.label}</div>
                  <div className="seopl-bulk-field__value">
                    <span className="seopl-bulk-field__auto">리뷰 데이터 기반 자동 생성</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="seopl-bulk-fields">
                <div className="seopl-bulk-fields__head">
                  <span>적용</span><span>필드</span><span>값/템플릿</span>
                </div>
                {fieldDefs.map(f => (
                  <div key={f.key} className={`seopl-bulk-field${m.enabled[f.key] ? ' is-on' : ''}`}>
                    <div className="seopl-bulk-field__check"><input type="checkbox" checked={!!m.enabled[f.key]} onChange={() => this.toggleBulkField(f.key)}/></div>
                    <div className="seopl-bulk-field__label">{f.label}</div>
                    <div className="seopl-bulk-field__value">
                      {f.type === 'textarea' ? (
                        <FormTextarea rows={2} placeholder={f.placeholder} value={m.template[f.key] || ''} disabled={!m.enabled[f.key]} onChange={(e) => this.onBulkTemplateChange(f.key, e.target.value)}/>
                      ) : f.type === 'select' ? (
                        <FormSelect value={m.template[f.key] || 'T'} disabled={!m.enabled[f.key]} onChange={(e) => this.onBulkTemplateChange(f.key, e.target.value)}>
                          {SE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </FormSelect>
                      ) : (
                        <FormInput placeholder={f.placeholder} value={m.template[f.key] || ''} disabled={!m.enabled[f.key]} onChange={(e) => this.onBulkTemplateChange(f.key, e.target.value)}/>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {samples.length > 0 && (
                <div className="seopl-bulk-preview">
                  <div className="seopl-bulk-preview__head">미리보기 (현재 페이지의 첫 {samples.length}개 상품)</div>
                  {(() => {
                    const enabledKeys = Object.keys(m.enabled).filter(k => m.enabled[k]);
                    if (enabledKeys.length === 0) return <div className="seopl-bulk-preview__empty">적용할 필드를 선택하세요.</div>;
                    return samples.map((sp, i) => (
                      <div key={i} className="seopl-bulk-preview__item">
                        <div className="seopl-bulk-preview__pname">{sp.productName}</div>
                        <ul>
                          {enabledKeys.map(k => {
                            const f = fieldDefs.find(x => x.key === k);
                            const v = this.applyTemplate(m.template[k], sp);
                            return <li key={k}><strong>{f.label}:</strong> <span>{v || <em>(빈값)</em>}</span></li>;
                          })}
                        </ul>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button theme="secondary" onClick={this.closeBulk}>취소</Button>
          <Button theme="primary"
                  onClick={this.startBulk}
                  disabled={!this.isPaidUser}
                  title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
            <FeatherIcon icon="play" size="14"/> 일괄 적용 시작
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  renderBulkProgress = () => {
    // 일괄 설정 진행 오버레이 — 분석 오버레이와 동일한 풀스크린 + SVG 로더 + optimistic tick
    const p = this.bulkProgress;
    if (!p.open) return null;
    const total = p.total || 0;
    const displayed = p.isRunning ? Math.floor(this.bulkOptimisticDone) : (p.done || 0);
    const pct = total > 0 ? Math.min(100, Math.round((displayed / total) * 100)) : 0;
    const phaseLabel = p.isCompleted
      ? (p.failed > 0 ? '완료 (일부 실패)' : '완료')
      : p.isCancelling
        ? '중지 중...'
        : '일괄 SEO 설정 적용 중';

    return (
      <div className="seopl-overlay" role="dialog" aria-modal="true" aria-labelledby="seopl-bulk-overlay-title">
        <div className="seopl-overlay__backdrop"/>
        <div className="seopl-overlay__card">
          {this.renderLoaderSvg()}

          <h3 id="seopl-bulk-overlay-title" className="seopl-overlay__title">일괄 SEO 설정</h3>
          <div className="seopl-overlay__phase">{phaseLabel}</div>

          <div className="seopl-overlay__count">
            <span className="seopl-overlay__num">{displayed.toLocaleString()}</span>
            <span className="seopl-overlay__denom"> / {total.toLocaleString()}개</span>
          </div>

          <div className="seopl-overlay__progress">
            <div className="seopl-overlay__progress-fill" style={{width: pct + '%'}}/>
          </div>
          <div className="seopl-overlay__pct">{pct}%</div>

          <div className="seopl-overlay__sub-counts">
            성공 <strong className="ok">{p.success}</strong> · 실패 <strong className="err">{p.failed}</strong>
          </div>

          {p.failedList.length > 0 && (
            <div className="seopl-overlay__failed">
              <div className="seopl-overlay__failed-head">실패 목록 (최근 {Math.min(p.failedList.length, 5)}건)</div>
              <ul>
                {p.failedList.slice(-5).map((f, i) => (
                  <li key={i}><strong>{f.name}</strong> — {f.error}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="seopl-overlay__actions">
            {p.isRunning && !p.isCancelling && (
              <Button theme="light" size="sm" className="seopl-overlay__cancel" onClick={this.cancelBulk}>
                중지
              </Button>
            )}
            {(p.isCompleted || (p.isCancelling && !p.isRunning)) && (
              <Button theme="primary" size="sm" onClick={() => { this.closeBulkProgress(); this.loadList(); }}>
                닫기
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const totalPages = this.effectiveTotalPages;
    const page = this.isClientFilterMode
      ? this.listParams.page
      : ((this.list && this.list.page) ? this.list.page : 1);

    return (
      <div className="main-content">
        <PageTitle/>

        <div className="seopl-page">
          <div className="seopl-header">
            <div className="seopl-header__left">
              <h2 className="seopl-header__title">상품별 SEO 설정</h2>
              <p className="seopl-header__desc">상품 단위로 검색엔진 메타 정보를 관리합니다. 좌측 칩으로 누락 항목을 빠르게 찾아 수정하세요.</p>
            </div>
            <div className="seopl-header__right">
              <Button theme="light" size="sm"
                      onClick={this.rerunAnalysis}
                      disabled={this.analysis.isRunning}
                      title="카페24에서 모든 상품의 SEO를 다시 가져옵니다">
                <FeatherIcon icon="refresh-cw" size="13"/>
                {this.analysis.isRunning && this.analysis.mode === 'cafe24' ? '재분석 중...' : '재분석'}
              </Button>
              <Button theme="primary" size="sm"
                      onClick={() => { this.bulkModal.scope = 'all'; this.openBulk(); }}
                      disabled={!this.isPaidUser}
                      title={!this.isPaidUser ? '유료 플랜 전용 기능입니다.' : ''}>
                <FeatherIcon icon="layers" size="13"/> 일괄 설정
              </Button>
            </div>
          </div>

          {!this.isPaidUser && (
            <div className="seopl-paywall-banner">
              <FeatherIcon icon="lock" size="14"/>
              <span><strong>진단·조회는 무료로 이용</strong>할 수 있습니다. 상품 SEO 편집·저장·일괄 설정은 <strong>유료 플랜 전용</strong>입니다. <em>(14일 무료 체험 포함)</em></span>
              <button type="button" className="seopl-paywall-banner__cta"
                      onClick={() => { window.location.href = '/inAppOrder'; }}>
                <FeatherIcon icon="credit-card" size="13"/>
                결제페이지로 이동하기
                <FeatherIcon icon="arrow-right" size="13"/>
              </button>
            </div>
          )}

          {this.renderHealthCard()}
          {this.renderFilterBar()}
          {this.renderSelectionBar()}
          {this.renderTable()}

          {totalPages > 1 && (
            <div className="seopl-pagination">
              <Button size="sm" theme="light" disabled={page <= 1} onClick={() => this.goPage(page - 1)}><FeatherIcon icon="chevron-left" size="14"/></Button>
              <span className="seopl-pagination__info">{page} / {totalPages}</span>
              <Button size="sm" theme="light" disabled={page >= totalPages} onClick={() => this.goPage(page + 1)}><FeatherIcon icon="chevron-right" size="14"/></Button>
            </div>
          )}

          {this.renderPanel()}
          {this.renderBulkModal()}
          {this.renderBulkProgress()}
          {this.renderAnalysisOverlay()}
        </div>
      </div>
    );
  }
}

export default SeoProductList;
